import React, { useState, Fragment } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { MapPin, Calendar, DollarSign, TrendingUp, AlertCircle, CheckCircle2, Clock, Users, Mail, FileText, ChevronRight, Sparkles, Plus, MessageSquare, Handshake, Activity, Filter, Download } from 'lucide-react';
type TabType = 'overview' | 'route' | 'targets' | 'outreach' | 'replies' | 'deals' | 'activity';
export function CampaignDetailPage() {
  const {
    id
  } = useParams();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="max-w-7xl mx-auto" data-id="element-1995">
      {/* Hero Header */}
      <div className="bg-surface border border-border rounded-xl p-6 mb-6" data-id="element-1996">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4" data-id="element-1997">
          <div data-id="element-1998">
            <div className="flex items-center gap-3 mb-2" data-id="element-1999">
              <h1 className="text-3xl font-bold" data-id="element-2000">Summer Run '26</h1>
              <span className="px-3 py-1 bg-green-500/10 text-green-500 text-sm rounded-lg font-medium" data-id="element-2001">
                Active
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-textMuted" data-id="element-2002">
              <div className="flex items-center gap-1.5" data-id="element-2003">
                <Users className="w-4 h-4" data-id="element-2004" />
                The Wildwoods
              </div>
              <div className="flex items-center gap-1.5" data-id="element-2005">
                <MapPin className="w-4 h-4" data-id="element-2006" />
                Midwest → New England
              </div>
              <div className="flex items-center gap-1.5" data-id="element-2007">
                <Calendar className="w-4 h-4" data-id="element-2008" />
                Jun 10 - Jun 25
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-2009">
            <button className="px-4 py-2 bg-surface border border-border rounded-lg hover:bg-surfaceHover transition-colors text-sm font-medium" data-id="element-2010">
              Edit Campaign
            </button>
            <button className="px-4 py-2 bg-primary text-background rounded-lg hover:bg-primaryHover transition-colors text-sm font-medium flex items-center gap-2" data-id="element-2011">
              <Plus className="w-4 h-4" data-id="element-2012" />
              Add Targets
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-6 border-t border-border pt-4" data-id="element-2013">
          <button onClick={() => setActiveTab('overview')} className={`pb-2 text-sm font-medium transition-colors ${activeTab === 'overview' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-2014">
            Overview
          </button>
          <button onClick={() => setActiveTab('route')} className={`pb-2 text-sm font-medium transition-colors ${activeTab === 'route' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-2015">
            Route Planner
          </button>
          <button onClick={() => setActiveTab('targets')} className={`pb-2 text-sm font-medium transition-colors ${activeTab === 'targets' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-2016">
            Targets
          </button>
          <button onClick={() => setActiveTab('outreach')} className={`pb-2 text-sm font-medium transition-colors ${activeTab === 'outreach' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-2017">
            Outreach
          </button>
          <button onClick={() => setActiveTab('replies')} className={`pb-2 text-sm font-medium transition-colors ${activeTab === 'replies' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-2018">
            Replies
          </button>
          <button onClick={() => setActiveTab('deals')} className={`pb-2 text-sm font-medium transition-colors ${activeTab === 'deals' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-2019">
            Deals
          </button>
          <button onClick={() => setActiveTab('activity')} className={`pb-2 text-sm font-medium transition-colors ${activeTab === 'activity' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-2020">
            Activity
          </button>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && <div className="grid grid-cols-12 gap-6" data-id="element-2021">
          {/* Left Column */}
          <div className="col-span-12 lg:col-span-8 space-y-6" data-id="element-2022">
            {/* Booking Funnel */}
            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-2023">
              <h3 className="text-lg font-semibold mb-4" data-id="element-2024">Booking Funnel</h3>
              <div className="flex items-center gap-2 mb-4" data-id="element-2025">
                {[{
              label: 'Targets',
              value: 42,
              color: 'border'
            }, {
              label: 'Contacted',
              value: 28,
              color: 'blue'
            }, {
              label: 'Replied',
              value: 14,
              color: 'purple'
            }, {
              label: 'Offers',
              value: 6,
              color: 'primary'
            }, {
              label: 'Confirmed',
              value: 3,
              color: 'green'
            }].map((stage, i) => <Fragment key={stage.label} data-id="element-2026">
                    <div className="flex-1 text-center" data-id="element-2027">
                      <div className={`text-2xl font-bold mb-1 ${stage.color === 'border' ? 'text-textMuted' : `text-${stage.color}-500`}`} data-id="element-2028">
                        {stage.value}
                      </div>
                      <div className="text-xs text-textMuted" data-id="element-2029">
                        {stage.label}
                      </div>
                    </div>
                    {i < 4 && <ChevronRight className="w-4 h-4 text-textMuted shrink-0" data-id="element-2030" />}
                  </Fragment>)}
              </div>
              <div className="relative h-3 bg-background rounded-full overflow-hidden" data-id="element-2031">
                <div className="absolute inset-0 flex" data-id="element-2032">
                  <div className="h-full bg-blue-500" style={{
                width: '66%'
              }} data-id="element-2033"></div>
                  <div className="h-full bg-purple-500" style={{
                width: '50%'
              }} data-id="element-2034"></div>
                  <div className="h-full bg-primary" style={{
                width: '43%'
              }} data-id="element-2035"></div>
                  <div className="h-full bg-green-500" style={{
                width: '50%'
              }} data-id="element-2036"></div>
                </div>
              </div>
            </div>

            {/* Route Gaps & Risks */}
            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-2037">
              <div className="flex items-center justify-between mb-4" data-id="element-2038">
                <h3 className="text-lg font-semibold" data-id="element-2039">Route Gaps & Risks</h3>
                <button className="text-sm text-primary hover:text-primaryHover flex items-center gap-1" data-id="element-2040">
                  View Route <ChevronRight className="w-4 h-4" data-id="element-2041" />
                </button>
              </div>
              <div className="space-y-3" data-id="element-2042">
                {[{
              date: 'Jun 16',
              location: 'Buffalo area',
              issue: 'No venue confirmed',
              severity: 'high'
            }, {
              date: 'Jun 19',
              location: 'Burlington area',
              issue: 'Lineup conflict',
              severity: 'medium'
            }, {
              date: 'Jun 22',
              location: 'Portland area',
              issue: 'Below minimum fee',
              severity: 'low'
            }].map((gap, i) => <div key={i} className={`p-3 rounded-lg border ${gap.severity === 'high' ? 'bg-red-500/5 border-red-500/20' : gap.severity === 'medium' ? 'bg-amber-500/5 border-amber-500/20' : 'bg-blue-500/5 border-blue-500/20'}`} data-id="element-2043">
                    <div className="flex items-start gap-3" data-id="element-2044">
                      <AlertCircle className={`w-5 h-5 shrink-0 mt-0.5 ${gap.severity === 'high' ? 'text-red-500' : gap.severity === 'medium' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-2045" />
                      <div className="flex-1" data-id="element-2046">
                        <div className="flex items-center justify-between mb-1" data-id="element-2047">
                          <span className="text-sm font-medium" data-id="element-2048">
                            {gap.date}
                          </span>
                          <span className="text-xs text-textMuted" data-id="element-2049">
                            {gap.location}
                          </span>
                        </div>
                        <div className="text-xs text-textMuted" data-id="element-2050">
                          {gap.issue}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Deals Awaiting Attention */}
            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-2051">
              <div className="flex items-center justify-between mb-4" data-id="element-2052">
                <h3 className="text-lg font-semibold" data-id="element-2053">
                  Deals Awaiting Attention
                </h3>
                <button className="text-sm text-primary hover:text-primaryHover flex items-center gap-1" data-id="element-2054">
                  View All <ChevronRight className="w-4 h-4" data-id="element-2055" />
                </button>
              </div>
              <div className="space-y-3" data-id="element-2056">
                {[{
              venue: 'The Empty Bottle',
              action: 'Review offer',
              value: '$1,500',
              flag: 'Below policy'
            }, {
              venue: 'Beachland Tavern',
              action: 'Confirm lineup',
              value: '$1,800',
              flag: 'Lineup conflict'
            }, {
              venue: 'Higher Ground',
              action: 'Sign contract',
              value: '$2,200',
              flag: null
            }].map((deal, i) => <div key={i} className="flex items-center justify-between p-3 bg-background rounded-lg hover:bg-surfaceHover transition-colors cursor-pointer" data-id="element-2057">
                    <div className="flex-1" data-id="element-2058">
                      <div className="text-sm font-medium mb-1" data-id="element-2059">
                        {deal.venue}
                      </div>
                      <div className="text-xs text-textMuted" data-id="element-2060">
                        {deal.action}
                      </div>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-2061">
                      {deal.flag && <span className="px-2 py-1 bg-red-500/10 text-red-500 text-xs rounded" data-id="element-2062">
                          {deal.flag}
                        </span>}
                      <span className="text-sm font-medium text-primary" data-id="element-2063">
                        {deal.value}
                      </span>
                      <ChevronRight className="w-4 h-4 text-textMuted" data-id="element-2064" />
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="col-span-12 lg:col-span-4 space-y-6" data-id="element-2065">
            {/* Next Actions */}
            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-2066">
              <h3 className="text-lg font-semibold mb-4" data-id="element-2067">Next Actions</h3>
              <div className="space-y-3" data-id="element-2068">
                {[{
              task: 'Send follow-up to 5 venues',
              done: false
            }, {
              task: 'Review Empty Bottle offer',
              done: false
            }, {
              task: 'Confirm Tom for Jun 14 show',
              done: false
            }, {
              task: 'Sign Beachland contract',
              done: true
            }].map((action, i) => <div key={i} className="flex items-start gap-3" data-id="element-2069">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${action.done ? 'bg-green-500 border-green-500' : 'border-border'}`} data-id="element-2070">
                      {action.done && <CheckCircle2 className="w-3 h-3 text-background" data-id="element-2071" />}
                    </div>
                    <span className={`text-sm ${action.done ? 'text-textMuted line-through' : ''}`} data-id="element-2072">
                      {action.task}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-2073">
              <h3 className="text-lg font-semibold mb-4" data-id="element-2074">Recent Activity</h3>
              <div className="space-y-4" data-id="element-2075">
                {[{
              time: '2h ago',
              event: 'Offer received from The Empty Bottle',
              type: 'offer'
            }, {
              time: '5h ago',
              event: 'Sarah confirmed for Jun 12-18',
              type: 'lineup'
            }, {
              time: '1d ago',
              event: 'Contract signed: Beachland',
              type: 'contract'
            }, {
              time: '2d ago',
              event: 'Deposit received: Higher Ground',
              type: 'payment'
            }].map((activity, i) => <div key={i} className="flex items-start gap-3 pb-3 border-b border-border last:border-0" data-id="element-2076">
                    <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${activity.type === 'offer' ? 'bg-primary' : activity.type === 'lineup' ? 'bg-blue-500' : activity.type === 'contract' ? 'bg-green-500' : 'bg-green-500'}`} data-id="element-2077"></div>
                    <div className="flex-1 min-w-0" data-id="element-2078">
                      <p className="text-sm" data-id="element-2079">{activity.event}</p>
                      <p className="text-xs text-textMuted mt-1" data-id="element-2080">
                        {activity.time}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Financial Snapshot */}
            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-2081">
              <h3 className="text-lg font-semibold mb-4" data-id="element-2082">Financial Snapshot</h3>
              <div className="space-y-4" data-id="element-2083">
                <div data-id="element-2084">
                  <div className="flex items-center justify-between mb-2" data-id="element-2085">
                    <span className="text-sm text-textMuted" data-id="element-2086">
                      Expected Revenue
                    </span>
                    <span className="text-xl font-bold text-primary" data-id="element-2087">
                      $18,500
                    </span>
                  </div>
                  <div className="h-2 bg-background rounded-full overflow-hidden" data-id="element-2088">
                    <div className="h-full bg-primary" style={{
                  width: '75%'
                }} data-id="element-2089"></div>
                  </div>
                </div>
                <div data-id="element-2090">
                  <div className="flex items-center justify-between mb-2" data-id="element-2091">
                    <span className="text-sm text-textMuted" data-id="element-2092">
                      Deposits Collected
                    </span>
                    <span className="text-xl font-bold text-green-500" data-id="element-2093">
                      $4,200
                    </span>
                  </div>
                  <div className="h-2 bg-background rounded-full overflow-hidden" data-id="element-2094">
                    <div className="h-full bg-green-500" style={{
                  width: '23%'
                }} data-id="element-2095"></div>
                  </div>
                </div>
                <div data-id="element-2096">
                  <div className="flex items-center justify-between mb-2" data-id="element-2097">
                    <span className="text-sm text-textMuted" data-id="element-2098">Outstanding</span>
                    <span className="text-xl font-bold text-amber-500" data-id="element-2099">
                      $3,800
                    </span>
                  </div>
                  <div className="h-2 bg-background rounded-full overflow-hidden" data-id="element-2100">
                    <div className="h-full bg-amber-500" style={{
                  width: '21%'
                }} data-id="element-2101"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Replies Tab */}
      {activeTab === 'replies' && <div className="space-y-6" data-id="element-2102">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2103">
            <div className="bg-surface border border-border rounded-xl p-4" data-id="element-2104">
              <div className="text-sm text-textMuted mb-1" data-id="element-2105">Interested</div>
              <div className="text-2xl font-bold text-blue-500" data-id="element-2106">5</div>
            </div>
            <div className="bg-surface border border-border rounded-xl p-4" data-id="element-2107">
              <div className="text-sm text-textMuted mb-1" data-id="element-2108">Offers</div>
              <div className="text-2xl font-bold text-primary" data-id="element-2109">3</div>
            </div>
            <div className="bg-surface border border-border rounded-xl p-4" data-id="element-2110">
              <div className="text-sm text-textMuted mb-1" data-id="element-2111">Pass</div>
              <div className="text-2xl font-bold text-textMuted" data-id="element-2112">2</div>
            </div>
            <div className="bg-surface border border-border rounded-xl p-4" data-id="element-2113">
              <div className="text-sm text-textMuted mb-1" data-id="element-2114">Needs Follow-up</div>
              <div className="text-2xl font-bold text-amber-500" data-id="element-2115">4</div>
            </div>
          </div>

          <div className="bg-surface border border-border rounded-xl overflow-hidden" data-id="element-2116">
            <div className="p-4 border-b border-border flex items-center justify-between" data-id="element-2117">
              <h3 className="font-semibold" data-id="element-2118">Campaign Inbox</h3>
              <button className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm flex items-center gap-2 hover:bg-surfaceHover transition-colors" data-id="element-2119">
                <Filter className="w-4 h-4" data-id="element-2120" /> Filter
              </button>
            </div>
            <div className="divide-y divide-border" data-id="element-2121">
              {[{
            venue: 'The Empty Bottle',
            preview: 'We can do June 12th. Offer attached.',
            class: 'Offer',
            color: 'primary',
            time: '2h ago'
          }, {
            venue: 'Beachland Tavern',
            preview: 'Sounds good, let me check the calendar.',
            class: 'Interested',
            color: 'blue',
            time: '5h ago'
          }, {
            venue: 'Higher Ground',
            preview: 'Are you open to a support slot?',
            class: 'Needs Follow-up',
            color: 'amber',
            time: '1d ago'
          }, {
            venue: 'The Sinclair',
            preview: 'Sorry, we are fully booked that week.',
            class: 'Pass',
            color: 'textMuted',
            time: '2d ago'
          }].map((thread, i) => <div key={i} className="p-4 hover:bg-surfaceHover/50 transition-colors cursor-pointer flex items-start justify-between gap-4 group" data-id="element-2122">
                  <div className="flex-1 min-w-0" data-id="element-2123">
                    <div className="flex items-center gap-3 mb-1" data-id="element-2124">
                      <h4 className="font-medium" data-id="element-2125">{thread.venue}</h4>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-${thread.color === 'textMuted' ? 'background' : thread.color + '-500/10'} text-${thread.color === 'textMuted' ? 'textMuted' : thread.color + '-500'} border border-${thread.color === 'textMuted' ? 'border' : thread.color + '-500/20'}`} data-id="element-2126">
                        {thread.class}
                      </span>
                    </div>
                    <p className="text-sm text-textMuted truncate" data-id="element-2127">
                      {thread.preview}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2" data-id="element-2128">
                    <span className="text-xs text-textMuted" data-id="element-2129">
                      {thread.time}
                    </span>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2" data-id="element-2130">
                      <button className="text-xs font-medium text-primary hover:text-primaryHover" data-id="element-2131">
                        Create Deal
                      </button>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>}

      {/* Deals Tab */}
      {activeTab === 'deals' && <div className="space-y-6" data-id="element-2132">
          <div className="flex items-center gap-2 overflow-x-auto pb-2" data-id="element-2133">
            <div className="px-4 py-2 bg-surface border border-border rounded-lg whitespace-nowrap" data-id="element-2134">
              <span className="text-sm text-textMuted mr-2" data-id="element-2135">Prospect</span>
              <span className="font-bold" data-id="element-2136">3</span>
            </div>
            <div className="px-4 py-2 bg-surface border border-border rounded-lg whitespace-nowrap" data-id="element-2137">
              <span className="text-sm text-textMuted mr-2" data-id="element-2138">Offer</span>
              <span className="font-bold text-primary" data-id="element-2139">4</span>
            </div>
            <div className="px-4 py-2 bg-surface border border-border rounded-lg whitespace-nowrap" data-id="element-2140">
              <span className="text-sm text-textMuted mr-2" data-id="element-2141">Hold</span>
              <span className="font-bold text-amber-500" data-id="element-2142">2</span>
            </div>
            <div className="px-4 py-2 bg-surface border border-border rounded-lg whitespace-nowrap" data-id="element-2143">
              <span className="text-sm text-textMuted mr-2" data-id="element-2144">Contract Sent</span>
              <span className="font-bold text-blue-500" data-id="element-2145">1</span>
            </div>
            <div className="px-4 py-2 bg-surface border border-border rounded-lg whitespace-nowrap" data-id="element-2146">
              <span className="text-sm text-textMuted mr-2" data-id="element-2147">Confirmed</span>
              <span className="font-bold text-green-500" data-id="element-2148">2</span>
            </div>
          </div>

          <div className="bg-surface border border-border rounded-xl overflow-hidden" data-id="element-2149">
            <table className="w-full text-sm text-left" data-id="element-2150">
              <thead className="text-xs text-textMuted uppercase bg-background/50 border-b border-border" data-id="element-2151">
                <tr data-id="element-2152">
                  <th className="px-6 py-4 font-medium" data-id="element-2153">Venue</th>
                  <th className="px-6 py-4 font-medium" data-id="element-2154">Date</th>
                  <th className="px-6 py-4 font-medium" data-id="element-2155">Value Range</th>
                  <th className="px-6 py-4 font-medium" data-id="element-2156">Stage</th>
                  <th className="px-6 py-4 font-medium" data-id="element-2157">Lineup</th>
                  <th className="px-6 py-4 font-medium" data-id="element-2158">Deposit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border" data-id="element-2159">
                {[{
              venue: 'The Empty Bottle',
              date: 'Jun 12',
              value: '$1,500 - $2,000',
              stage: 'Confirmed',
              lineup: 'Locked',
              deposit: 'Paid'
            }, {
              venue: 'Beachland Tavern',
              date: 'Jun 14',
              value: '$1,200',
              stage: 'Contract Sent',
              lineup: 'Locked',
              deposit: 'Pending'
            }, {
              venue: 'Higher Ground',
              date: 'Jun 18',
              value: '$2,000',
              stage: 'Hold',
              lineup: 'Pending',
              deposit: 'N/A'
            }, {
              venue: 'The Sinclair',
              date: 'Jun 22',
              value: '$2,500',
              stage: 'Offer',
              lineup: 'Checking',
              deposit: 'N/A'
            }].map((deal, i) => <tr key={i} className="hover:bg-surfaceHover/50 transition-colors cursor-pointer" data-id="element-2160">
                    <td className="px-6 py-4 font-medium" data-id="element-2161">{deal.venue}</td>
                    <td className="px-6 py-4" data-id="element-2162">{deal.date}</td>
                    <td className="px-6 py-4" data-id="element-2163">{deal.value}</td>
                    <td className="px-6 py-4" data-id="element-2164">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${deal.stage === 'Confirmed' ? 'bg-green-500/10 text-green-500' : deal.stage === 'Hold' ? 'bg-amber-500/10 text-amber-500' : deal.stage === 'Contract Sent' ? 'bg-blue-500/10 text-blue-500' : 'bg-primary/10 text-primary'}`} data-id="element-2165">
                        {deal.stage}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-2166">
                      <span className={`text-xs ${deal.lineup === 'Locked' ? 'text-green-500' : deal.lineup === 'Pending' ? 'text-amber-500' : 'text-textMuted'}`} data-id="element-2167">
                        {deal.lineup}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-2168">
                      <span className={`text-xs ${deal.deposit === 'Paid' ? 'text-green-500' : deal.deposit === 'Pending' ? 'text-amber-500' : 'text-textMuted'}`} data-id="element-2169">
                        {deal.deposit}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {/* Activity Tab */}
      {activeTab === 'activity' && <div className="max-w-3xl mx-auto" data-id="element-2170">
          <div className="flex items-center justify-between mb-6" data-id="element-2171">
            <div className="flex gap-2" data-id="element-2172">
              <button className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm font-medium" data-id="element-2173">
                All
              </button>
              <button className="px-3 py-1.5 bg-surface border border-border text-textMuted hover:text-textMain rounded-lg text-sm font-medium transition-colors" data-id="element-2174">
                Outreach
              </button>
              <button className="px-3 py-1.5 bg-surface border border-border text-textMuted hover:text-textMain rounded-lg text-sm font-medium transition-colors" data-id="element-2175">
                Replies
              </button>
              <button className="px-3 py-1.5 bg-surface border border-border text-textMuted hover:text-textMain rounded-lg text-sm font-medium transition-colors" data-id="element-2176">
                Deals
              </button>
            </div>
            <button className="px-3 py-1.5 bg-surface border border-border rounded-lg text-sm flex items-center gap-2 hover:bg-surfaceHover transition-colors" data-id="element-2177">
              <Download className="w-4 h-4" data-id="element-2178" /> Export
            </button>
          </div>

          <div className="bg-surface border border-border rounded-xl p-6" data-id="element-2179">
            <div className="space-y-6" data-id="element-2180">
              <div className="relative" data-id="element-2181">
                <div className="absolute left-[11px] top-6 bottom-0 w-px bg-border" data-id="element-2182"></div>
                <div className="flex items-start gap-4" data-id="element-2183">
                  <div className="w-6 h-6 rounded-full bg-surface border-2 border-primary flex items-center justify-center shrink-0 z-10" data-id="element-2184">
                    <Handshake className="w-3 h-3 text-primary" data-id="element-2185" />
                  </div>
                  <div data-id="element-2186">
                    <p className="text-sm" data-id="element-2187">
                      <span className="font-medium" data-id="element-2188">Deal Confirmed:</span> The
                      Empty Bottle
                    </p>
                    <p className="text-xs text-textMuted mt-1" data-id="element-2189">
                      Today, 2:30 PM
                    </p>
                  </div>
                </div>
              </div>
              <div className="relative" data-id="element-2190">
                <div className="absolute left-[11px] top-6 bottom-0 w-px bg-border" data-id="element-2191"></div>
                <div className="flex items-start gap-4" data-id="element-2192">
                  <div className="w-6 h-6 rounded-full bg-surface border-2 border-blue-500 flex items-center justify-center shrink-0 z-10" data-id="element-2193">
                    <MessageSquare className="w-3 h-3 text-blue-500" data-id="element-2194" />
                  </div>
                  <div data-id="element-2195">
                    <p className="text-sm" data-id="element-2196">
                      <span className="font-medium" data-id="element-2197">Reply Received:</span>{' '}
                      Beachland Tavern
                    </p>
                    <p className="text-xs text-textMuted mt-1" data-id="element-2198">
                      Yesterday, 4:15 PM
                    </p>
                  </div>
                </div>
              </div>
              <div className="relative" data-id="element-2199">
                <div className="absolute left-[11px] top-6 bottom-0 w-px bg-border" data-id="element-2200"></div>
                <div className="flex items-start gap-4" data-id="element-2201">
                  <div className="w-6 h-6 rounded-full bg-surface border-2 border-amber-500 flex items-center justify-center shrink-0 z-10" data-id="element-2202">
                    <MapPin className="w-3 h-3 text-amber-500" data-id="element-2203" />
                  </div>
                  <div data-id="element-2204">
                    <p className="text-sm" data-id="element-2205">
                      <span className="font-medium" data-id="element-2206">Route Updated:</span> Added
                      Burlington, VT
                    </p>
                    <p className="text-xs text-textMuted mt-1" data-id="element-2207">
                      Jun 2, 10:00 AM
                    </p>
                  </div>
                </div>
              </div>
              <div className="relative" data-id="element-2208">
                <div className="flex items-start gap-4" data-id="element-2209">
                  <div className="w-6 h-6 rounded-full bg-surface border-2 border-textMuted flex items-center justify-center shrink-0 z-10" data-id="element-2210">
                    <Mail className="w-3 h-3 text-textMuted" data-id="element-2211" />
                  </div>
                  <div data-id="element-2212">
                    <p className="text-sm" data-id="element-2213">
                      <span className="font-medium" data-id="element-2214">Outreach Sent:</span> 42
                      targets
                    </p>
                    <p className="text-xs text-textMuted mt-1" data-id="element-2215">
                      Jun 1, 9:00 AM
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Route Planner Tab */}
      {activeTab === 'route' && <div className="flex gap-6" data-id="element-2216">
          {/* Left Sidebar */}
          <div className="w-64 shrink-0 space-y-4" data-id="element-2217">
            <div className="bg-surface border border-border rounded-xl p-4" data-id="element-2218">
              <h4 className="text-sm font-semibold mb-3" data-id="element-2219">Route Version</h4>
              <div className="space-y-2" data-id="element-2220">
                {['Safe', 'Balanced', 'Upside'].map(version => <button key={version} className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${version === 'Balanced' ? 'bg-primary/10 text-primary' : 'bg-background text-textMuted hover:text-textMain'}`} data-id="element-2221">
                    {version}
                  </button>)}
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-4" data-id="element-2222">
              <h4 className="text-sm font-semibold mb-3" data-id="element-2223">Route Settings</h4>
              <div className="space-y-3 text-sm" data-id="element-2224">
                <div data-id="element-2225">
                  <div className="text-textMuted mb-1" data-id="element-2226">Date Range</div>
                  <div className="font-medium" data-id="element-2227">Jun 10 - Jun 25</div>
                </div>
                <div data-id="element-2228">
                  <div className="text-textMuted mb-1" data-id="element-2229">Region</div>
                  <div className="font-medium" data-id="element-2230">Midwest → NE</div>
                </div>
                <div data-id="element-2231">
                  <div className="text-textMuted mb-1" data-id="element-2232">Max Drive</div>
                  <div className="font-medium" data-id="element-2233">300 mi/day</div>
                </div>
              </div>
            </div>
          </div>

          {/* Center - Map */}
          <div className="flex-1" data-id="element-2234">
            <div className="bg-surface border border-border rounded-xl p-6 mb-4" data-id="element-2235">
              <div className="relative h-96 border border-border rounded-lg bg-background/50 overflow-hidden" data-id="element-2236">
                <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(#27272A 1px, transparent 1px)',
              backgroundSize: '16px 16px',
              opacity: 0.2
            }} data-id="element-2237"></div>
                {/* Cities */}
                <div className="absolute top-1/2 left-[15%] w-4 h-4 rounded-full bg-primary shadow-[0_0_12px_rgba(245,158,11,0.6)]" data-id="element-2238">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium whitespace-nowrap" data-id="element-2239">
                    Chicago
                  </div>
                </div>
                <div className="absolute top-[45%] left-[35%] w-4 h-4 rounded-full bg-primary shadow-[0_0_12px_rgba(245,158,11,0.6)]" data-id="element-2240">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium whitespace-nowrap" data-id="element-2241">
                    Cleveland
                  </div>
                </div>
                <div className="absolute top-[40%] left-[50%] w-4 h-4 rounded-full bg-primary shadow-[0_0_12px_rgba(245,158,11,0.6)]" data-id="element-2242">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium whitespace-nowrap" data-id="element-2243">
                    Buffalo
                  </div>
                </div>
                <div className="absolute top-[25%] left-[70%] w-4 h-4 rounded-full bg-primary shadow-[0_0_12px_rgba(245,158,11,0.6)]" data-id="element-2244">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium whitespace-nowrap" data-id="element-2245">
                    Burlington
                  </div>
                </div>
                <div className="absolute top-[30%] left-[82%] w-4 h-4 rounded-full bg-primary shadow-[0_0_12px_rgba(245,158,11,0.6)]" data-id="element-2246">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium whitespace-nowrap" data-id="element-2247">
                    Cambridge
                  </div>
                </div>
                <div className="absolute top-[15%] right-[8%] w-4 h-4 rounded-full bg-border border-2 border-primary" data-id="element-2248">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium whitespace-nowrap text-textMuted" data-id="element-2249">
                    Portland
                  </div>
                </div>

                {/* Route lines */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-2250">
                  <path d="M 15% 50% L 35% 45% L 50% 40% L 70% 25% L 82% 30%" fill="none" stroke="#F59E0B" strokeWidth="2" strokeDasharray="4 4" className="opacity-60" data-id="element-2251" />
                  <path d="M 82% 30% L 92% 15%" fill="none" stroke="#F59E0B" strokeWidth="2" strokeDasharray="4 4" className="opacity-30" data-id="element-2252" />
                </svg>
              </div>
            </div>

            {/* Timeline Strip */}
            <div className="bg-surface border border-border rounded-xl p-4" data-id="element-2253">
              <div className="flex items-center gap-2 overflow-x-auto" data-id="element-2254">
                {[{
              date: 'Jun 12',
              city: 'Chicago',
              status: 'confirmed'
            }, {
              date: 'Jun 14',
              city: 'Cleveland',
              status: 'hold'
            }, {
              date: 'Jun 16',
              city: 'Buffalo',
              status: 'gap'
            }, {
              date: 'Jun 18',
              city: 'Burlington',
              status: 'hold'
            }, {
              date: 'Jun 21',
              city: 'Cambridge',
              status: 'confirmed'
            }, {
              date: 'Jun 24',
              city: 'Portland',
              status: 'gap'
            }].map((stop, i) => <Fragment key={i} data-id="element-2255">
                    <div className={`px-3 py-2 rounded-lg text-center shrink-0 ${stop.status === 'confirmed' ? 'bg-green-500/10 border border-green-500/20' : stop.status === 'hold' ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-red-500/10 border border-red-500/20'}`} data-id="element-2256">
                      <div className="text-xs text-textMuted mb-1" data-id="element-2257">
                        {stop.date}
                      </div>
                      <div className="text-sm font-medium" data-id="element-2258">{stop.city}</div>
                    </div>
                    {i < 5 && <ChevronRight className="w-4 h-4 text-textMuted shrink-0" data-id="element-2259" />}
                  </Fragment>)}
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="w-80 shrink-0 space-y-4" data-id="element-2260">
            <div className="bg-surface border border-border rounded-xl p-4" data-id="element-2261">
              <h4 className="text-sm font-semibold mb-3" data-id="element-2262">Route Economics</h4>
              <div className="space-y-3" data-id="element-2263">
                <div className="flex justify-between text-sm" data-id="element-2264">
                  <span className="text-textMuted" data-id="element-2265">Total Miles</span>
                  <span className="font-medium" data-id="element-2266">1,847 mi</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-2267">
                  <span className="text-textMuted" data-id="element-2268">Avg Drive</span>
                  <span className="font-medium" data-id="element-2269">132 mi</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-2270">
                  <span className="text-textMuted" data-id="element-2271">Cost/Show</span>
                  <span className="font-medium" data-id="element-2272">$485</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-2273">
                  <span className="text-textMuted" data-id="element-2274">Revenue/Mile</span>
                  <span className="font-medium text-primary" data-id="element-2275">$10.02</span>
                </div>
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-4" data-id="element-2276">
              <h4 className="text-sm font-semibold mb-3" data-id="element-2277">Selected Leg</h4>
              <div className="space-y-3" data-id="element-2278">
                <div data-id="element-2279">
                  <div className="text-xs text-textMuted mb-1" data-id="element-2280">Route</div>
                  <div className="text-sm font-medium" data-id="element-2281">Chicago → Cleveland</div>
                </div>
                <div data-id="element-2282">
                  <div className="text-xs text-textMuted mb-1" data-id="element-2283">Distance</div>
                  <div className="text-sm font-medium" data-id="element-2284">350 miles • 5h 20m</div>
                </div>
                <div data-id="element-2285">
                  <div className="text-xs text-textMuted mb-1" data-id="element-2286">Leg Cost</div>
                  <div className="text-sm font-medium" data-id="element-2287">$535</div>
                </div>
                <div data-id="element-2288">
                  <div className="text-xs text-textMuted mb-1" data-id="element-2289">
                    Show Revenue
                  </div>
                  <div className="text-sm font-medium text-primary" data-id="element-2290">$1,500</div>
                </div>
              </div>
            </div>

            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4" data-id="element-2291">
              <div className="flex items-start gap-2 mb-2" data-id="element-2292">
                <AlertCircle className="w-5 h-5 text-red-500 shrink-0" data-id="element-2293" />
                <h4 className="text-sm font-semibold text-red-500" data-id="element-2294">
                  Route Gap
                </h4>
              </div>
              <p className="text-xs text-textMuted" data-id="element-2295">
                Jun 16 has no confirmed venue. Buffalo area needs a booking.
              </p>
            </div>
          </div>
        </div>}

      {/* Targets Tab */}
      {activeTab === 'targets' && <>
          {/* Filter Bar */}
          <div className="flex flex-wrap items-center gap-3 mb-6" data-id="element-2296">
            <select className="px-3 py-2 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary text-textMuted" data-id="element-2297">
              <option data-id="element-2298">All Cities</option>
              <option data-id="element-2299">Chicago</option>
              <option data-id="element-2300">Cleveland</option>
              <option data-id="element-2301">Buffalo</option>
            </select>
            <select className="px-3 py-2 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary text-textMuted" data-id="element-2302">
              <option data-id="element-2303">Any Capacity</option>
              <option data-id="element-2304">200-500</option>
              <option data-id="element-2305">500+</option>
            </select>
            <select className="px-3 py-2 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary text-textMuted" data-id="element-2306">
              <option data-id="element-2307">Any Genre</option>
              <option data-id="element-2308">Folk</option>
              <option data-id="element-2309">Indie</option>
            </select>
            <select className="px-3 py-2 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary text-textMuted" data-id="element-2310">
              <option data-id="element-2311">Any Fit Score</option>
              <option data-id="element-2312">90%+</option>
              <option data-id="element-2313">80%+</option>
            </select>
            <div className="ml-auto" data-id="element-2314">
              <button className="px-4 py-2 bg-surface border border-border rounded-lg text-sm font-medium hover:bg-surfaceHover transition-colors" data-id="element-2315">
                Compare Selected
              </button>
            </div>
          </div>

          {/* Targets Table */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden" data-id="element-2316">
            <div className="overflow-x-auto" data-id="element-2317">
              <table className="w-full text-sm text-left" data-id="element-2318">
                <thead className="bg-surface border-b border-border text-textMuted" data-id="element-2319">
                  <tr data-id="element-2320">
                    <th className="px-6 py-4" data-id="element-2321">
                      <input type="checkbox" className="rounded" data-id="element-2322" />
                    </th>
                    <th className="px-6 py-4 font-medium" data-id="element-2323">Venue</th>
                    <th className="px-6 py-4 font-medium" data-id="element-2324">City</th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-2325">
                      Capacity
                    </th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-2326">
                      Fit Score
                    </th>
                    <th className="px-6 py-4 font-medium" data-id="element-2327">Contact Quality</th>
                    <th className="px-6 py-4 font-medium" data-id="element-2328">Last Verified</th>
                    <th className="px-6 py-4 font-medium" data-id="element-2329">Route Leg</th>
                    <th className="px-6 py-4 font-medium" data-id="element-2330">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-background" data-id="element-2331">
                  {[{
                venue: 'Higher Ground',
                city: 'Burlington, VT',
                capacity: 400,
                fit: 94,
                contact: 'excellent',
                verified: '2 weeks ago',
                leg: 'Leg 3'
              }, {
                venue: 'The Sinclair',
                city: 'Cambridge, MA',
                capacity: 525,
                fit: 91,
                contact: 'good',
                verified: '1 month ago',
                leg: 'Leg 4'
              }, {
                venue: 'ArtsRiot',
                city: 'Burlington, VT',
                capacity: 250,
                fit: 89,
                contact: 'fair',
                verified: '3 weeks ago',
                leg: 'Leg 3'
              }, {
                venue: 'The Empty Bottle',
                city: 'Chicago, IL',
                capacity: 400,
                fit: 86,
                contact: 'excellent',
                verified: '1 week ago',
                leg: 'Leg 1'
              }, {
                venue: 'Beachland Tavern',
                city: 'Cleveland, OH',
                capacity: 300,
                fit: 82,
                contact: 'good',
                verified: '2 months ago',
                leg: 'Leg 2'
              }].map((venue, i) => <tr key={i} className="hover:bg-surface/50 transition-colors" data-id="element-2332">
                      <td className="px-6 py-4" data-id="element-2333">
                        <input type="checkbox" className="rounded" data-id="element-2334" />
                      </td>
                      <td className="px-6 py-4 font-medium" data-id="element-2335">{venue.venue}</td>
                      <td className="px-6 py-4 text-textMuted" data-id="element-2336">{venue.city}</td>
                      <td className="px-6 py-4 text-right text-textMuted" data-id="element-2337">
                        {venue.capacity}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-2338">
                        <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded font-medium" data-id="element-2339">
                          {venue.fit}%
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-2340">
                        <div className="flex gap-0.5" data-id="element-2341">
                          {[1, 2, 3].map(dot => <div key={dot} className={`w-1.5 h-1.5 rounded-full ${venue.contact === 'excellent' || venue.contact === 'good' && dot <= 2 || venue.contact === 'fair' && dot === 1 ? 'bg-green-500' : 'bg-border'}`} data-id="element-2342"></div>)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-textMuted text-xs" data-id="element-2343">
                        {venue.verified}
                      </td>
                      <td className="px-6 py-4" data-id="element-2344">
                        <span className="px-2 py-1 bg-surface border border-border rounded text-xs" data-id="element-2345">
                          {venue.leg}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-2346">
                        <div className="flex items-center gap-2" data-id="element-2347">
                          <button className="px-2 py-1 bg-primary/10 text-primary text-xs rounded hover:bg-primary/20 transition-colors" data-id="element-2348">
                            Add
                          </button>
                          <button className="px-2 py-1 bg-surface border border-border text-xs rounded hover:bg-surfaceHover transition-colors" data-id="element-2349">
                            Shortlist
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </>}

      {/* Outreach Tab */}
      {activeTab === 'outreach' && <div className="flex gap-6" data-id="element-2350">
          {/* Left - Sequence Builder */}
          <div className="w-80 shrink-0 space-y-4" data-id="element-2351">
            <div className="bg-surface border border-border rounded-xl p-4" data-id="element-2352">
              <div className="flex items-center justify-between mb-4" data-id="element-2353">
                <h4 className="text-sm font-semibold" data-id="element-2354">Sequence Steps</h4>
                <button className="text-xs text-primary hover:text-primaryHover" data-id="element-2355">
                  + Add Step
                </button>
              </div>
              <div className="space-y-3" data-id="element-2356">
                {[{
              step: 1,
              name: 'Initial Outreach',
              status: 'sent',
              count: 18
            }, {
              step: 2,
              name: 'Follow-up (3 days)',
              status: 'scheduled',
              count: 7
            }, {
              step: 3,
              name: 'Final follow-up (7 days)',
              status: 'pending',
              count: 0
            }].map(step => <div key={step.step} className={`p-3 rounded-lg border cursor-pointer transition-colors ${step.step === 1 ? 'bg-primary/5 border-primary/30' : 'bg-background border-border hover:border-primary/30'}`} data-id="element-2357">
                    <div className="flex items-center gap-2 mb-2" data-id="element-2358">
                      <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${step.step === 1 ? 'bg-primary/20 text-primary' : 'bg-surface text-textMuted'}`} data-id="element-2359">
                        {step.step}
                      </div>
                      <div className="text-sm font-medium flex-1" data-id="element-2360">
                        {step.name}
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs" data-id="element-2361">
                      <span className={`px-2 py-0.5 rounded ${step.status === 'sent' ? 'bg-green-500/10 text-green-500' : step.status === 'scheduled' ? 'bg-blue-500/10 text-blue-500' : 'bg-border text-textMuted'}`} data-id="element-2362">
                        {step.status}
                      </span>
                      <span className="text-textMuted" data-id="element-2363">
                        {step.count} venues
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-4" data-id="element-2364">
              <h4 className="text-sm font-semibold mb-3" data-id="element-2365">Template</h4>
              <select className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:border-primary" data-id="element-2366">
                <option data-id="element-2367">Standard Booking Inquiry</option>
                <option data-id="element-2368">Festival Pitch</option>
                <option data-id="element-2369">Follow-up Template</option>
              </select>
            </div>
          </div>

          {/* Center - Message Preview */}
          <div className="flex-1 bg-surface border border-border rounded-xl p-6" data-id="element-2370">
            <div className="flex items-center justify-between mb-4" data-id="element-2371">
              <h4 className="text-lg font-semibold" data-id="element-2372">Message Preview</h4>
              <div className="flex items-center gap-2" data-id="element-2373">
                <button className="px-3 py-1.5 bg-background border border-border rounded-lg text-xs font-medium hover:bg-surfaceHover transition-colors flex items-center gap-1" data-id="element-2374">
                  <Sparkles className="w-3 h-3" data-id="element-2375" /> AI Improve
                </button>
                <button className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-medium hover:bg-primary/20 transition-colors" data-id="element-2376">
                  Edit Template
                </button>
              </div>
            </div>

            <div className="bg-background border border-border rounded-lg p-4 text-sm leading-relaxed" data-id="element-2377">
              <div className="mb-4" data-id="element-2378">
                <div className="text-xs text-textMuted mb-2" data-id="element-2379">Subject:</div>
                <div className="font-medium" data-id="element-2380">
                  June tour dates — The Wildwoods in{' '}
                  <span className="bg-primary/20 text-primary px-1 rounded" data-id="element-2381">
                    {'{'}venue_city{'}'}
                  </span>
                </div>
              </div>

              <div className="space-y-3 text-textMuted" data-id="element-2382">
                <p data-id="element-2383">
                  Hi{' '}
                  <span className="bg-primary/20 text-primary px-1 rounded" data-id="element-2384">
                    {'{'}venue_contact{'}'}
                  </span>
                  ,
                </p>

                <p data-id="element-2385">
                  I'm reaching out on behalf of The Wildwoods, a folk/americana
                  act currently routing through{' '}
                  <span className="bg-primary/20 text-primary px-1 rounded" data-id="element-2386">
                    {'{'}region{'}'}
                  </span>{' '}
                  this June.
                </p>

                <p data-id="element-2387">
                  We're looking at{' '}
                  <span className="bg-primary/20 text-primary px-1 rounded" data-id="element-2388">
                    {'{'}date_range{'}'}
                  </span>{' '}
                  and would love to play{' '}
                  <span className="bg-primary/20 text-primary px-1 rounded" data-id="element-2389">
                    {'{'}venue_name{'}'}
                  </span>
                  . Our typical draw in similar markets is 150-250, and we work
                  on a guarantee or door split.
                </p>

                <p data-id="element-2390">Are any of those dates open on your calendar?</p>

                <p data-id="element-2391">
                  Best,
                  <br data-id="element-2392" />
                  Mark Thompson
                  <br data-id="element-2393" />
                  The Wildwoods
                </p>
              </div>
            </div>
          </div>

          {/* Right - Audience & Settings */}
          <div className="w-80 shrink-0 space-y-4" data-id="element-2394">
            <div className="bg-surface border border-border rounded-xl p-4" data-id="element-2395">
              <h4 className="text-sm font-semibold mb-3" data-id="element-2396">Audience Summary</h4>
              <div className="space-y-3" data-id="element-2397">
                <div className="flex justify-between text-sm" data-id="element-2398">
                  <span className="text-textMuted" data-id="element-2399">Total Venues</span>
                  <span className="font-medium" data-id="element-2400">25</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-2401">
                  <span className="text-textMuted" data-id="element-2402">Sent</span>
                  <span className="font-medium text-green-500" data-id="element-2403">18</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-2404">
                  <span className="text-textMuted" data-id="element-2405">Pending</span>
                  <span className="font-medium text-amber-500" data-id="element-2406">7</span>
                </div>
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-4" data-id="element-2407">
              <h4 className="text-sm font-semibold mb-3" data-id="element-2408">Send Rules</h4>
              <div className="space-y-3" data-id="element-2409">
                <label className="flex items-center gap-2 text-sm" data-id="element-2410">
                  <input type="checkbox" className="rounded" defaultChecked data-id="element-2411" />
                  <span data-id="element-2412">Personalize per venue</span>
                </label>
                <label className="flex items-center gap-2 text-sm" data-id="element-2413">
                  <input type="checkbox" className="rounded" defaultChecked data-id="element-2414" />
                  <span data-id="element-2415">Auto follow-up</span>
                </label>
                <label className="flex items-center gap-2 text-sm" data-id="element-2416">
                  <input type="checkbox" className="rounded" data-id="element-2417" />
                  <span data-id="element-2418">Stop on reply</span>
                </label>
              </div>
            </div>

            <div className="bg-primary/10 border border-primary/20 rounded-xl p-4" data-id="element-2419">
              <div className="flex items-center gap-2 mb-2" data-id="element-2420">
                <CheckCircle2 className="w-5 h-5 text-primary" data-id="element-2421" />
                <h4 className="text-sm font-semibold text-primary" data-id="element-2422">
                  Ready to Send
                </h4>
              </div>
              <p className="text-xs text-textMuted mb-3" data-id="element-2423">
                7 venues queued for outreach
              </p>
              <button className="w-full px-4 py-2 bg-primary text-background rounded-lg hover:bg-primaryHover transition-colors text-sm font-medium" data-id="element-2424">
                Send Outreach
              </button>
            </div>
          </div>
        </div>}
    </motion.div>;
}